import { type ReactNode, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useMountEffect } from 'hooks/useMountEffect'
import { cn } from 'components/UI/cn'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { getModalZIndex } from '../../styles/z-index'

interface BottomSheetProps {
  /** Called when the user dismisses by tapping the overlay or pressing the close button. */
  onClose: () => void
  /** Optional title shown at the top of the sheet. */
  title?: string
  /** Sheet body. */
  children: ReactNode
  /** Optional className for the sheet container. */
  className?: string
}

/**
 * Bottom-sheet primitive for mobile flows.
 *
 * Visual: slides up from the bottom, fills full width, rounded top corners,
 * up to 90vh tall with internal scroll, dimmed overlay.
 *
 * Use for: mobile confirms, action sheets, mobile-only forms.
 * Do not use on desktop — the design tokens assume a thumb-reachable layout.
 *
 * Implementation note: this is a standalone portaled component rather than a
 * wrapper around the shared <Modal />. The shared react-bootstrap Modal centers
 * its dialog and doesn't expose a clean override path for bottom-anchored
 * positioning, and the Radix-based <Dialog> primitive hardcodes
 * `top-[50%] translate-y-[-50%]` plus zoom/slide-from-top animations on
 * <DialogContent>. Overriding both required several `!` modifiers and still
 * left animation classes fighting our slide-up transform. We still participate
 * in the modal-stack system via `useModalDepth()` + `getModalZIndex()` so that
 * sheets layered on top of an existing modal pick the correct z-index.
 */
export function BottomSheet({
  onClose,
  title,
  children,
  className,
}: BottomSheetProps) {
  const parentDepth = useModalDepth()
  const sheetZIndex = useMemo(
    () => getModalZIndex(parentDepth + 1),
    [parentDepth]
  )

  // Lock body scroll for the lifetime of the sheet. Callers must conditionally
  // render BottomSheet so mount === visible; this effect's cleanup restores
  // scroll on unmount.
  useMountEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  })

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 flex flex-col justify-end"
      style={{ zIndex: sheetZIndex }}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-modal-overlay"
      />
      <div
        className={cn(
          'relative w-full max-h-[90vh] overflow-y-auto rounded-t-2xl bg-surface shadow-[0_-8px_32px_rgba(0,0,0,0.16)]',
          className
        )}
      >
        {/* Drag handle / visual affordance */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="h-1 w-10 rounded-full bg-line" />
        </div>
        {title && (
          <div className="flex items-center justify-between px-4 pb-2">
            <h2 className="text-base font-semibold text-fg">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="-mr-2 flex h-8 w-8 items-center justify-center rounded-full text-fg-secondary hover:bg-surface-hover"
            >
              <X size={20} />
            </button>
          </div>
        )}
        <div className="px-4 pb-6">{children}</div>
      </div>
    </div>,
    document.body
  )
}
